import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Download, Mail, X } from "lucide-react";

export default function AdminAssessments() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [paidOnly, setPaidOnly] = useState(false);
  const [preview, setPreview] = useState(null); // {subject, html, sample, type, result_id}
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/assessments`, { params: { paid_only: paidOnly } }).then((r) => {
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [paidOnly]);

  const download = () => {
    const url = `${API}/admin/assessments.csv${paidOnly ? "?paid_only=true" : ""}`;
    window.location.href = url;
  };

  const openPreview = async (type = "paid", result_id = "") => {
    setPreviewLoading(true);
    try {
      const params = { type };
      if (result_id) params.result_id = result_id;
      const r = await axios.get(`${API}/admin/emails/preview`, { params });
      setPreview({ ...r.data, result_id });
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not load email preview");
    } finally { setPreviewLoading(false); }
  };

  return (
    <div data-testid="admin-assessments">
      <div className="flex items-center justify-between mb-8 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">All results</div>
          <h1 className="font-serif text-3xl text-[#383633] tracking-tight">
            Assessments <span className="text-[#6F6B66] font-serif ml-2">{total}</span>
          </h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-xs kt-tracking uppercase text-[#6F6B66] flex items-center gap-2">
            <input type="checkbox" checked={paidOnly} onChange={(e) => setPaidOnly(e.target.checked)} data-testid="paid-only" /> Paid only
          </label>
          <Button variant="outline" onClick={() => openPreview("paid", "")} disabled={previewLoading}
            data-testid="preview-sample-paid-email"
            className="rounded-full border-[#383633] text-[#383633] hover:bg-[#F8F6F2] text-xs kt-tracking uppercase">
            <Mail className="h-4 w-4 mr-2" /> Preview sample paid email
          </Button>
          <Button variant="outline" onClick={download} data-testid="csv-btn"
            className="rounded-full border-[#383633] text-[#383633] hover:bg-[#F8F6F2] text-xs kt-tracking uppercase">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="bg-white border kt-line rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F6F2] text-[#6F6B66]">
            <tr>
              <th className="text-left px-4 py-3 kt-tracking uppercase text-[10px]">Name</th>
              <th className="text-left px-4 py-3 kt-tracking uppercase text-[10px]">Email</th>
              <th className="text-left px-4 py-3 kt-tracking uppercase text-[10px]">Lang</th>
              <th className="text-left px-4 py-3 kt-tracking uppercase text-[10px]">Score</th>
              <th className="text-left px-4 py-3 kt-tracking uppercase text-[10px]">Priority</th>
              <th className="text-left px-4 py-3 kt-tracking uppercase text-[10px]">Status</th>
              <th className="text-left px-4 py-3 kt-tracking uppercase text-[10px]">Date</th>
              <th className="text-right px-4 py-3 kt-tracking uppercase text-[10px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.result_id} className="border-t kt-line" data-testid={`assessment-row-${r.result_id}`}>
                <td className="px-4 py-3 text-[#383633]">{r.first_name}</td>
                <td className="px-4 py-3 text-[#6F6B66]">{r.email}</td>
                <td className="px-4 py-3 text-[#6F6B66] uppercase kt-tracking text-xs">{r.language}</td>
                <td className="px-4 py-3 font-mono">{r.free_preview?.overall_score ?? "-"}</td>
                <td className="px-4 py-3 text-[#383633]">{r.free_preview?.main_priority_title ?? "-"}</td>
                <td className="px-4 py-3">
                  {r.plan_purchased ? (
                    <span className="text-xs kt-tracking uppercase text-[#A8AD94]">
                      {r.unlock_reason === "complimentary" ? "Complimentary" : "Paid"}
                    </span>
                  ) : (
                    <span className="text-xs kt-tracking uppercase text-[#6F6B66]">Free preview</span>
                  )}
                </td>
                <td className="px-4 py-3 text-[#6F6B66] font-mono text-xs">{r.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => openPreview("paid", r.result_id)}
                    disabled={previewLoading}
                    data-testid={`preview-paid-email-${r.result_id}`}
                    className="inline-flex items-center gap-1 text-xs kt-tracking uppercase text-[#6F6B66] hover:text-[#383633]"
                  >
                    <Mail className="h-3 w-3" /> Preview email
                  </button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#6F6B66]">No assessments yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto"
             onClick={() => setPreview(null)}>
          <div className="bg-white max-w-3xl w-full my-8 rounded-2xl border kt-line overflow-hidden"
               onClick={(e) => e.stopPropagation()} data-testid="email-preview-modal">
            <div className="flex items-center justify-between p-5 border-b kt-line">
              <div>
                <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">
                  Email preview {preview.sample ? "· sample" : ""}
                </div>
                <h2 className="font-serif text-xl text-[#383633] mt-1" data-testid="email-preview-subject">
                  {preview.subject}
                </h2>
                <div className="text-[11px] text-[#6F6B66] mt-1">
                  Type: {preview.type === "paid" ? "30-day program ready (post-payment)" : "Free preview"}
                  {preview.result_id && <span> · Result: <code>{preview.result_id.slice(0, 8)}…</code></span>}
                </div>
              </div>
              <button onClick={() => setPreview(null)} className="text-[#6F6B66] hover:text-[#383633]">
                <X className="h-4 w-4" />
              </button>
            </div>
            <iframe
              title="email-preview"
              data-testid="email-preview-iframe"
              srcDoc={preview.html}
              className="w-full h-[70vh] bg-[#F8F6F2]"
              sandbox=""
            />
          </div>
        </div>
      )}
    </div>
  );
}
